/**
 * Centralized Metric Help Explanations for Reports & Operational Analytics -> Management Summary
 * 
 * Written in plain end-user language for farm owners and managers (Cez).
 * Answers "What is this?" and "How is it worked out?" based strictly on actual business logic.
 */

export interface MetricHelpEntry {
  title: string;
  description: string;
}

export const MANAGEMENT_SUMMARY_HELP: Record<string, MetricHelpEntry> = {
  // 1. Financial Snapshot
  bookedPigSales: {
    title: 'Booked Pig Sales',
    description:
      'Total gross revenue from commercial pig sales recorded in the General Ledger (Account 1010) during the selected Reporting Year. This represents calendar cashflow posted in this financial period.',
  },
  bookedProductionCosts: {
    title: 'Booked Production Costs',
    description:
      'Total direct production expenses booked in the General Ledger (Accounts 3010–3051) during the selected calendar year, including feeds, piglet stock, veterinary medicines, trucking, and farm supplies.',
  },
  completedCyclesGrossProfit: {
    title: 'Completed-Cycle Gross Profit',
    description:
      'True lifecycle profitability of production cycles that completed (settled or closed) within the selected year. It is calculated by taking total sales from those closed cycles and subtracting all production costs belonging to those same cycles.',
  },
  completedCyclesGrossMarginPct: {
    title: 'Completed-Cycle Gross Margin %',
    description:
      'The percentage of completed-cycle sales that remained as gross profit (Completed Gross Profit ÷ Completed Sales Revenue). Unlike calendar cashflow, this reflects the true batch margin of finished pig crops.',
  },

  // 2. Current Production
  activeCycles: {
    title: 'Active Cycles',
    description:
      'The number of ongoing production batches currently running in grower pens. These cycles have arrived piglets actively feeding and growing, and have not yet completed final settlement.',
  },
  livePigsOnFeed: {
    title: 'Live Pigs on Feed',
    description:
      'Current live herd count across all active production cycles. It is calculated from source-backed arrival placements plus transfers in, minus recorded mortalities, transfers out, and scale sales.',
  },
  pigsSold: {
    title: 'Pigs Sold',
    description:
      'Total number of commercial market hogs sold at the scale chute for cycles belonging to the selected Reporting Year. Reflects verified weighing ticket records.',
  },
  mortality: {
    title: 'Mortality & Mortality Rate',
    description:
      'Total recorded pig deaths in the selected cohort. The mortality percentage (Deaths ÷ Starting Placed Head) is calculated only when verified initial placement headcount exists; missing starting counts are shown as — and never guessed.',
  },

  // 3. Grower Settlement Position
  finalizedSettlements: {
    title: 'Finalized Settlements',
    description:
      'The number of completed grower contracts whose financial liquidation statements have been permanently frozen and locked after accounting audit.',
  },
  approvedSettlements: {
    title: 'Approved Settlements',
    description:
      'Settlements that have been reviewed and approved by supervisors, but are still awaiting final management freeze and liquidation disbursement.',
  },
  draftSettlements: {
    title: 'Draft Settlements',
    description:
      'Settlements currently in progress or undergoing calculation adjustments. These remain fully editable by operations personnel.',
  },
  amountOutstanding: {
    title: 'Amount Outstanding',
    description:
      'Total calculated grower payout obligations across existing Draft and Approved settlements that have not yet been finalized. This reflects actual calculated statements and does not estimate uncalculated cycles.',
  },

  // 4. Completed Cycle Performance Highlights & Table Headings
  bestMargin: {
    title: 'Best Margin Cycle',
    description:
      'Identifies the finished production cycle that achieved the highest gross margin percentage within the selected reporting year.',
  },
  lowestMargin: {
    title: 'Lowest Margin Cycle',
    description:
      'Identifies the finished production cycle with the lowest gross margin percentage. This highlights the least profitable batch for management review and cost analysis.',
  },
  lowestMortality: {
    title: 'Lowest Mortality Cycle',
    description:
      'Highlights the completed cycle with the lowest verified mortality rate among batches where verified starting placement headcount is on record.',
  },
  tableCycleCode: {
    title: 'Production Cycle',
    description:
      'Unique identifier and batch code assigned to each contract production cycle from intake to harvest.',
  },
  tableRaiserName: {
    title: 'Contract Raiser / Grower',
    description:
      'The grower partner or farm facility responsible for raising and caring for this batch of pigs.',
  },
  tableSoldHead: {
    title: 'Pigs Marketed (Sold Head)',
    description:
      'Total number of market pigs sold from this completed cycle across all commercial scale weighing tickets.',
  },
  tableTotalRevenue: {
    title: 'Sales Revenue',
    description:
      'Total gross income earned from selling pigs in this cycle (Net Weight kg × Price per kg minus tare shrinkage).',
  },
  tableGrossProfit: {
    title: 'Gross Profit',
    description:
      'Sales Revenue minus total direct costs (piglet stock, feed deliveries, medicines, and cycle-specific expenses) for this batch.',
  },
  tableGrossMarginPct: {
    title: 'Gross Margin %',
    description:
      'Percentage of sales revenue retained as profit (Gross Profit ÷ Sales Revenue) for this individual cycle.',
  },

  // 5. Production Cost Mix & Movement
  costFeed: {
    title: 'Feed Deliveries & Usage (Account 3020/3021)',
    description:
      'Cost of starter, grower, and finisher commercial feeds delivered to and consumed by production cycles in the selected calendar year.',
  },
  costPiglets: {
    title: 'Piglet Stock & Arrivals (Account 3010/3012/3015)',
    description:
      'Direct purchase cost of weaners and feeder piglets placed into pens during the selected reporting year.',
  },
  costMedication: {
    title: 'Veterinary & Medication (Account 3030–3034)',
    description:
      'Cost of vaccines, antibiotics, vitamins, and veterinary care supplies booked during the selected period.',
  },
  costOther: {
    title: 'Farm Operations & Trucking (Account 3040/3050/3051)',
    description:
      'Direct production support costs, including livestock hauling/trucking, farm permits, bedding, and operating supplies.',
  },
  costMetricAmount: {
    title: 'Booked Cost Amount',
    description:
      'Total expense for this cost category posted in the General Ledger during the selected Reporting Year.',
  },
  costMetricPct: {
    title: 'Share of Total Production Costs',
    description:
      'The percentage of all direct production costs represented by this category during the selected period.',
  },
  costMetricYoY: {
    title: 'Year-over-Year (YoY) Movement',
    description:
      'Percentage change compared to the previous calendar year (e.g. 2026 vs 2025). When All Years is selected, multi-year comparison is shown as —.',
  },

  // 6. Management Attention
  attentionNeedsReview: {
    title: 'Needs Review Backlog',
    description:
      'Active count of unassigned or ambiguous records in the Review & Resolve triage queue requiring management verification.',
  },
  attentionIncompleteRecords: {
    title: 'Incomplete Cycle Records',
    description:
      'Production cycles in the cohort missing verified placement headcount or placement start dates in the source records.',
  },
  attentionOutstandingSettlements: {
    title: 'Unsettled Cycles',
    description:
      'Contract grower cycles that have harvested and sold pigs but have not yet undergone final settlement liquidation.',
  },
  attentionCycleIssues: {
    title: 'Cycle-Linked Issues',
    description:
      'Unresolved triage queue items directly connected to specific production cycles that should be resolved before finalizing accounts.',
  },
};
